/*global google*/
import { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMap } from '@vis.gl/react-google-maps';

import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import {
  TerraDraw,
  TerraDrawCircleMode,
  TerraDrawPolygonMode
} from 'terra-draw';

const convertPolygon = (feature, options) => {
  const { geometry: { coordinates } } = feature;
  return new google.maps.Polygon({
    ...options,
    paths: coordinates.map(ring => ring.map(lngLat => new google.maps.LatLng(lngLat[1], lngLat[0])))
  });
};

// Terra Draw stores circles as approximated polygons; reconstruct a google.maps.Circle
// so callers can access .center and .radius as expected.
const convertCircle = (feature, options) => {
  const { geometry: { coordinates } } = feature;
  const ring = coordinates[0];
  const lats = ring.map(coord => coord[1]);
  const lngs = ring.map(coord => coord[0]);
  const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
  const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
  const center = new google.maps.LatLng(centerLat, centerLng);
  const firstPoint = new google.maps.LatLng(ring[0][1], ring[0][0]);
  const radius = google.maps.geometry.spherical.computeDistanceBetween(center, firstPoint);
  return new google.maps.Circle({ ...options, center, radius });
};

const TerraDrawReactGoogleMapsDrawingManager = ({
  drawingMode,
  options,
  onCircleComplete,
  onPolygonComplete
}) => {
  const map = useMap();
  const [enabledDrawing, setEnabledDrawing] = useState(null);

  const drawing = useMemo(() => {
    if (!map) {
      return null;
    }
    const {
      circleOptions = {},
      polygonOptions = {}
    } = options;
    const modes = [];
    if (onCircleComplete) {
      const {
        strokeColor = '#000000',
        strokeOpacity = 1.0,
        strokeWidth = 2,
        fillColor = '#000000',
        fillOpacity = 0.3
      } = circleOptions;
      modes.push(new TerraDrawCircleMode({
        styles: {
          outlineColor: strokeColor,
          outlineOpacity: strokeOpacity,
          outlineWidth: strokeWidth,
          fillColor,
          fillOpacity
        }
      }));
    }
    if (onPolygonComplete) {
      const {
        strokeColor = '#000000',
        strokeOpacity = 1.0,
        strokeWidth = 2,
        fillColor = '#000000',
        fillOpacity = 0.3
      } = polygonOptions;
      modes.push(new TerraDrawPolygonMode({
        styles: {
          outlineColor: strokeColor,
          outlineOpacity: strokeOpacity,
          outlineWidth: strokeWidth,
          fillColor,
          fillOpacity
        }
      }));
    }
    return new TerraDraw({
      adapter: new TerraDrawGoogleMapsAdapter({
        lib: google.maps,
        map,
        coordinatePrecision: 9
      }),
      modes
    });
  }, [map, options, onCircleComplete, onPolygonComplete]);

  const handleFinish = useCallback((finishedId) => {
    const {
      circleOptions = {},
      polygonOptions = {}
    } = options;
    const feature = drawing.getSnapshotFeature(finishedId);
    if (feature.properties.mode === 'circle') {
      const gFeature = convertCircle(feature, circleOptions);
      gFeature.setMap(map);
      drawing.clear();
      onCircleComplete?.(gFeature);
    }
    if (feature.properties.mode === 'polygon') {
      const gFeature = convertPolygon(feature, polygonOptions);
      gFeature.setMap(map);
      drawing.clear();
      onPolygonComplete?.(gFeature);
    }
  }, [map, options, onCircleComplete, onPolygonComplete, drawing]);

  useEffect(() => {
    if (enabledDrawing) {
      switch (drawingMode) {
      case 'polygon':
        enabledDrawing.clear();
        enabledDrawing.setMode('polygon');
        break;
      case 'circle':
        enabledDrawing.clear();
        enabledDrawing.setMode('circle');
        break;
      case null:
      default:
        enabledDrawing.clear();
        enabledDrawing.setMode('static');
        break;
      }
    }
  }, [enabledDrawing, drawingMode]);

  useEffect(() => {
    if (drawing) {
      const ready = () => setEnabledDrawing(drawing);
      drawing.start();
      drawing.setMode('static');
      drawing.clear();
      drawing.on('finish', handleFinish);
      drawing.on('ready', ready);
      return () => {
        setEnabledDrawing(null);
        drawing.off('ready', ready);
        drawing.off('finish', handleFinish);
        drawing.setMode('static');
        drawing.clear();
        drawing.stop();
      };
    }
    return () => {};
  }, [drawing, handleFinish]);

  return null;
};

TerraDrawReactGoogleMapsDrawingManager.propTypes = {
  drawingMode: PropTypes.string,
  onCircleComplete: PropTypes.func,
  onPolygonComplete: PropTypes.func,
  options: PropTypes.object
};

export default TerraDrawReactGoogleMapsDrawingManager;
